import "shared/ui/topbar/topbar.css";

import { apm } from "@elastic/apm-rum";
import { MenuItem, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { topbarStore } from "shared/common/stores/topbarStore";
import MenuWithPointerForwarding from "shared/ui/muiComponentWrappers/MenuWithPointerForwarding";

const LANGUAGES = [
  { code: "de", name: "Deutsch" },
  { code: "en", name: "English" },
  { code: "fr", name: "Français" },
  { code: "it", name: "Italiano" },
];

type Language = (typeof LANGUAGES)[number]["code"];

export default function LanguageSelector() {
  const darkTheme = defaultStore((state) => state.darkTheme);
  const anchorLang = topbarStore((state) => state.anchorLang);
  const changeAnchorLang = topbarStore((state) => state.changeAnchorLang);
  const isLanguageOpen = Boolean(anchorLang);
  const { i18n } = useTranslation();

  const handleLanguageClose = () => {
    changeAnchorLang(null);
  };

  const handleLanguageSelect = (lang: Language) => {
    monitor(apm, `set-language-${lang} | Language`, () => {
      i18n.changeLanguage(lang);
      handleLanguageClose();
    });
  };

  return (
    <MenuWithPointerForwarding
      anchorEl={anchorLang}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      id="Language"
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        padding: 0,
        marginTop: "6px",
      }}
      open={isLanguageOpen}
      onClose={handleLanguageClose}
      classes={{
        root: "indexForLanguage",
        list: darkTheme ? "infra3d-menuListDark" : "infra3d-menuListLight",
      }}
    >
      {LANGUAGES.map((lang) => (
        <MenuItem
          key={lang.code}
          sx={{
            gap: "10px",
            color: i18n.language.includes(lang.code)
              ? "secondary.main"
              : darkTheme
                ? "white"
                : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          onClick={() => handleLanguageSelect(lang.code)}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, minWidth: "24px" }}
          >
            {lang.code.toUpperCase()}
          </Typography>
          <Typography variant="body1">{lang.name}</Typography>
        </MenuItem>
      ))}
    </MenuWithPointerForwarding>
  );
}
